import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';

const ReportTemplateContext = createContext();

export const useReportTemplates = () => {
  const context = useContext(ReportTemplateContext);
  if (!context) {
    throw new Error('useReportTemplates must be used within a ReportTemplateProvider');
  }
  return context;
};

export const ReportTemplateProvider = ({ children }) => {
  const [reportTemplates, setReportTemplates] = useState([
    {
      id: 'prescription',
      name: 'Medication/Prescription Form',
      category: 'Clinical',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Prescription Form',
      footerText: 'This prescription is valid for 7 days',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1, fields: [] },
        { id: 'medications', name: 'Medications', enabled: true, order: 2, fields: [] },
        { id: 'instructions', name: 'Instructions', enabled: true, order: 3, fields: [] },
        { id: 'prescriber', name: 'Prescriber Details', enabled: true, order: 4, fields: [] }
      ]
    },
    {
      id: 'lab_results',
      name: 'Laboratory Results Report',
      category: 'Laboratory',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Laboratory Results',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1, fields: [] },
        { id: 'test_details', name: 'Test Details', enabled: true, order: 2, fields: [] },
        { id: 'results', name: 'Results', enabled: true, order: 3, fields: [] },
        { id: 'reference_ranges', name: 'Reference Ranges', enabled: true, order: 4, fields: [] },
        { id: 'technician', name: 'Lab Technician', enabled: true, order: 5, fields: [] }
      ]
    },
    {
      id: 'imaging_results',
      name: 'Imaging Results Report',
      category: 'Radiology',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Imaging Results',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1, fields: [] },
        { id: 'imaging_details', name: 'Imaging Details', enabled: true, order: 2, fields: [] },
        { id: 'findings', name: 'Findings', enabled: true, order: 3, fields: [] },
        { id: 'impression', name: 'Impression', enabled: true, order: 4, fields: [] },
        { id: 'radiologist', name: 'Radiologist', enabled: true, order: 5, fields: [] }
      ]
    },
    {
      id: 'discharge',
      name: 'Discharge Report',
      category: 'IPD',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Discharge Summary',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1, fields: [] },
        { id: 'admission', name: 'Admission Details', enabled: true, order: 2, fields: [] },
        { id: 'diagnosis', name: 'Diagnosis', enabled: true, order: 3, fields: [] },
        { id: 'treatment', name: 'Treatment Summary', enabled: true, order: 4, fields: [] },
        { id: 'medications', name: 'Discharge Medications', enabled: true, order: 5, fields: [] },
        { id: 'followup', name: 'Follow-up Instructions', enabled: true, order: 6, fields: [] }
      ]
    },
    {
      id: 'medical_report',
      name: 'Medical Report',
      category: 'Clinical',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Medical Report',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1 },
        { id: 'history', name: 'Medical History', enabled: true, order: 2 },
        { id: 'examination', name: 'Examination Findings', enabled: true, order: 3 },
        { id: 'diagnosis', name: 'Diagnosis', enabled: true, order: 4 },
        { id: 'recommendations', name: 'Recommendations', enabled: true, order: 5 }
      ]
    },
    {
      id: 'delivery_report',
      name: 'Delivery Report',
      category: 'Maternal & Child',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Delivery Report',
      footerText: '',
      signature: null,
      sections: [
        { id: 'mother', name: 'Mother Information', enabled: true, order: 1 },
        { id: 'delivery_details', name: 'Delivery Details', enabled: true, order: 2 },
        { id: 'baby', name: 'Baby Details', enabled: true, order: 3 },
        { id: 'complications', name: 'Complications', enabled: true, order: 4 },
        { id: 'attendant', name: 'Attendant Details', enabled: true, order: 5 }
      ]
    },
    {
      id: 'maternal',
      name: 'Comprehensive Maternal Health Report',
      category: 'Maternal & Child',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Maternal Health Report',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1, fields: [] },
        { id: 'obstetric_summary', name: 'Obstetric Summary', enabled: true, order: 2, fields: [] },
        { id: 'pregnancy_details', name: 'Pregnancy Details', enabled: true, order: 3, fields: [] },
        { id: 'anc_history', name: 'ANC Visit History', enabled: true, order: 4, fields: [] },
        { id: 'delivery_details', name: 'Delivery Details', enabled: true, order: 5, fields: [] },
        { id: 'admissions', name: 'IPD Admissions', enabled: true, order: 6, fields: [] },
        { id: 'postnatal_visits', name: 'Postnatal Visits', enabled: true, order: 7, fields: [] },
        { id: 'children', name: 'Children Registered', enabled: true, order: 8, fields: [] },
        { id: 'recommendations', name: 'Clinical Summary & Recommendations', enabled: true, order: 9, fields: [] }
      ]
    },
    {
      id: 'partograph',
      name: 'Partograph',
      category: 'Maternal & Child',
      paperSize: 'A4',
      orientation: 'landscape',
      margins: { top: 15, right: 15, bottom: 15, left: 15 },
      showHeader: true,
      showFooter: false,
      headerLogo: null,
      headerText: 'Partograph',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1 },
        { id: 'labour_progress', name: 'Labour Progress Chart', enabled: true, order: 2 },
        { id: 'fetal_monitoring', name: 'Fetal Monitoring', enabled: true, order: 3 },
        { id: 'maternal_vitals', name: 'Maternal Vitals', enabled: true, order: 4 }
      ]
    },
    {
      id: 'growth_chart',
      name: 'Child Growth Chart',
      category: 'Maternal & Child',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 15, right: 15, bottom: 15, left: 15 },
      showHeader: true,
      showFooter: false,
      headerLogo: null,
      headerText: 'Child Growth Chart',
      footerText: '',
      signature: null,
      sections: [
        { id: 'child', name: 'Child Information', enabled: true, order: 1 },
        { id: 'weight_chart', name: 'Weight Chart', enabled: true, order: 2 },
        { id: 'height_chart', name: 'Height Chart', enabled: true, order: 3 },
        { id: 'head_circumference', name: 'Head Circumference', enabled: true, order: 4 }
      ]
    },
    {
      id: 'immunisation_card',
      name: 'Immunisation Card',
      category: 'Maternal & Child',
      paperSize: 'A5',
      orientation: 'portrait',
      margins: { top: 10, right: 10, bottom: 10, left: 10 },
      showHeader: true,
      showFooter: false,
      headerLogo: null,
      headerText: 'Immunisation Record',
      footerText: '',
      signature: null,
      sections: [
        { id: 'child', name: 'Child Information', enabled: true, order: 1 },
        { id: 'schedule', name: 'Vaccination Schedule', enabled: true, order: 2 },
        { id: 'records', name: 'Vaccination Records', enabled: true, order: 3 }
      ]
    },
    {
      id: 'referral_form',
      name: 'Referral Form',
      category: 'Clinical',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'Patient Referral Form',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1, fields: [] },
        { id: 'referring_facility', name: 'Referring Facility', enabled: true, order: 2, fields: [] },
        { id: 'receiving_facility', name: 'Receiving Facility', enabled: true, order: 3, fields: [] },
        { id: 'reason', name: 'Reason for Referral', enabled: true, order: 4, fields: [] },
        { id: 'clinical_info', name: 'Clinical Information', enabled: true, order: 5, fields: [] },
        { id: 'referring_doctor', name: 'Referring Doctor', enabled: true, order: 6, fields: [] }
      ]
    },
    {
      id: 'external_lab_order',
      name: 'External Lab Order Form',
      category: 'Laboratory',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'External Laboratory Order',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1 },
        { id: 'tests', name: 'Tests Requested', enabled: true, order: 2 },
        { id: 'clinical_notes', name: 'Clinical Notes', enabled: true, order: 3 },
        { id: 'ordering_doctor', name: 'Ordering Doctor', enabled: true, order: 4 }
      ]
    },
    {
      id: 'external_imaging_order',
      name: 'External Imaging Order Form',
      category: 'Radiology',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'External Imaging Order',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1 },
        { id: 'imaging', name: 'Imaging Requested', enabled: true, order: 2 },
        { id: 'clinical_indication', name: 'Clinical Indication', enabled: true, order: 3 },
        { id: 'ordering_doctor', name: 'Ordering Doctor', enabled: true, order: 4 }
      ]
    },
    {
      id: 'external_procedure_order',
      name: 'External Procedure Order Form',
      category: 'Procedures',
      paperSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      showHeader: true,
      showFooter: true,
      headerLogo: null,
      headerText: 'External Procedure Order',
      footerText: '',
      signature: null,
      sections: [
        { id: 'patient', name: 'Patient Information', enabled: true, order: 1 },
        { id: 'procedure', name: 'Procedure Requested', enabled: true, order: 2 },
        { id: 'clinical_indication', name: 'Clinical Indication', enabled: true, order: 3 },
        { id: 'ordering_doctor', name: 'Ordering Doctor', enabled: true, order: 4 }
      ]
    }
  ]);

  // Load templates from localStorage on mount
  useEffect(() => {
    const savedTemplates = localStorage.getItem('reportTemplates');
    if (savedTemplates) {
      try {
        const parsed = JSON.parse(savedTemplates);
        // Migrate: ensure all sections have fields array
        const migrated = parsed.map(template => ({
          ...template,
          sections: (template.sections || []).map(section => ({
            ...section,
            fields: section.fields || [] // Ensure fields array exists
          }))
        }));
        setReportTemplates(migrated);
        // Save migrated version
        localStorage.setItem('reportTemplates', JSON.stringify(migrated));
      } catch (error) {
        console.error('Error loading saved templates:', error);
      }
    } else {
      // Use defaults
    }
  }, []);

  // Save templates to localStorage whenever they change
  const updateReportTemplates = (newTemplates) => {
    setReportTemplates(newTemplates);
    localStorage.setItem('reportTemplates', JSON.stringify(newTemplates));
  };

  const getTemplateById = useCallback((id) => {
    return reportTemplates.find(template => template.id === id);
  }, [reportTemplates]);

  const updateTemplate = useCallback((id, updatedTemplate) => {
    const newTemplates = reportTemplates.map(template =>
      template.id === id ? updatedTemplate : template
    );
    updateReportTemplates(newTemplates);
  }, [reportTemplates]);

  const value = useMemo(() => ({
    reportTemplates,
    setReportTemplates: updateReportTemplates,
    getTemplateById,
    updateTemplate
  }), [reportTemplates, updateReportTemplates, getTemplateById, updateTemplate]);

  return (
    <ReportTemplateContext.Provider value={value}>
      {children}
    </ReportTemplateContext.Provider>
  );
};
